<script setup lang="ts">
import { GamesApiError, GetGamesDashboard, GetKioskGames } from "@/api/gamesApi";
import GamesAudienceCard from "@/components/games/dashboard/GamesAudienceCard.vue";
import GamesBranchesCard from "@/components/games/dashboard/GamesBranchesCard.vue";
import GamesHoursCard from "@/components/games/dashboard/GamesHoursCard.vue";
import GamesRedemptionCard from "@/components/games/dashboard/GamesRedemptionCard.vue";
import GamesTopPrizesCard from "@/components/games/dashboard/GamesTopPrizesCard.vue";
import GamesTrendCard from "@/components/games/dashboard/GamesTrendCard.vue";
import GamesDemoAlert from "@/components/games/GamesDemoAlert.vue";
import PetDashboardStatCard from "@/components/pet-profile/dashboard/PetDashboardStatCard.vue";
import { useGamesFormat } from "@/composables/useGamesFormat";
import { resolveRelativeDateRange } from "@/composables/useFrappeListFilters";
import { emptyGamesDashboard, localDate, type GamesDashboard, type KioskGame } from "@/type/games";

/**
 * Games dashboard — how the kiosk games perform over a period: plays and winners, prize
 * redemption, busiest times, who plays, and branches/screens. One `get_dashboard` call per
 * filter change (docs/backend/kiosk-games.md §5.4).
 *
 * ⚠️ A failure never renders as zeros: "0 plays" reads as "nobody played", which is exactly
 * the wrong conclusion when the server simply didn't answer.
 */

definePage({ meta: { permissionKey: "page.games.dashboard" } });

const { t } = useI18n({ useScope: "global" });
const { formatInteger, formatPercent, gameTitle } = useGamesFormat();

// --- Period + game filter (local state: the report dashboards' range is a shared singleton) ---

const PRESETS = ["today", "last_7_days", "last_30_days", "this_month", "last_month", "custom"] as const;
type Preset = (typeof PRESETS)[number];

const preset = ref<Preset>("last_30_days");
const customFrom = ref<string>(localDate(new Date(Date.now() - 29 * 864e5)));
const customTo = ref<string>(localDate());
const gameFilter = ref<string | null>(null);

const presetItems = computed(() => PRESETS.map(value => ({ value, title: t(`alkokh.games.dashboard.period.${value}`) })));

const range = computed(() => {
  if (preset.value === "custom") {
    const from = customFrom.value || localDate();
    const to = customTo.value || from;

    return from <= to ? { dateFrom: from, dateTo: to } : { dateFrom: to, dateTo: from };
  }
  const resolved = resolveRelativeDateRange(preset.value);

  return resolved ? { dateFrom: resolved[0], dateTo: resolved[1] } : { dateFrom: localDate(), dateTo: localDate() };
});

const games = ref<KioskGame[]>([]);
const gameItems = computed(() => games.value.map(g => ({ value: g.name, title: gameTitle(g) })));

// --- Data -------------------------------------------------------------------------------

const data = ref<GamesDashboard>(emptyGamesDashboard());
const loading = ref(false);
const state = ref<"ok" | "failed" | "not_deployed">("ok");
const loaded = ref(false);

let requestSeq = 0;

async function load() {
  const seq = ++requestSeq;
  try {
    const result = await GetGamesDashboard(loading, { ...range.value, game: gameFilter.value });
    // A slower earlier request must not overwrite the answer for the current filters.
    if (seq !== requestSeq)
      return;
    data.value = result;
    state.value = "ok";
  }
  catch (error) {
    if (seq !== requestSeq)
      return;
    state.value = error instanceof GamesApiError && error.code === "not_deployed" ? "not_deployed" : "failed";
  }
  finally {
    if (seq === requestSeq)
      loaded.value = true;
  }
}

watch([range, gameFilter], load, { immediate: true, deep: true });

onMounted(async () => {
  try {
    games.value = await GetKioskGames(ref(false));
  }
  catch {
    games.value = [];
  }
});

// --- KPI tiles ---------------------------------------------------------------------------

const tiles = computed(() => {
  const s = data.value.totals;
  const rate = s.plays ? (s.redeemed / s.plays) * 100 : 0;

  return [
    { key: "plays", icon: "tabler-player-play", color: "primary", value: formatInteger(s.plays) },
    { key: "uniquePlayers", icon: "tabler-users", color: "info", value: formatInteger(s.unique_players) },
    { key: "won", icon: "tabler-trophy", color: "warning", value: formatInteger(s.won) },
    { key: "redeemed", icon: "tabler-circle-check", color: "success", value: formatInteger(s.redeemed) },
    { key: "redemptionRate", icon: "tabler-percentage", color: "success", value: formatPercent(rate) },
    { key: "expired", icon: "tabler-clock", color: "secondary", value: formatInteger(s.expired) },
    { key: "newPlayers", icon: "tabler-star", color: "primary", value: formatInteger(s.new_players) },
    { key: "registeredWinners", icon: "tabler-heart", color: "error", value: formatInteger(s.registered_winners) },
  ];
});

const showData = computed(() => state.value === "ok" && loaded.value);
const cardLoading = computed(() => loading.value && !loaded.value);
</script>

<template>
  <div class="games-dashboard">
    <div class="games-dashboard__head">
      <div>
        <h1 class="text-h4">
          {{ t("alkokh.games.dashboard.title") }}
        </h1>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ t("alkokh.games.dashboard.subtitle") }}
        </p>
      </div>
      <VBtn
        variant="tonal"
        prepend-icon="tabler-trophy"
        to="/games"
      >
        {{ t("alkokh.games.list.title") }}
      </VBtn>
    </div>

    <GamesDemoAlert @reset="load" />

    <VCard
      elevation="0"
      border
      class="mb-4"
    >
      <VCardText class="games-dashboard__filters">
        <AppSelect
          v-model="preset"
          :items="presetItems"
          density="compact"
          :label="t('alkokh.games.dashboard.period.label')"
          class="games-dashboard__filter"
        />
        <template v-if="preset === 'custom'">
          <AppDateTimePicker
            v-model="customFrom"
            density="compact"
            :label="t('alkokh.games.dashboard.period.from')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            class="games-dashboard__filter"
          />
          <AppDateTimePicker
            v-model="customTo"
            density="compact"
            :label="t('alkokh.games.dashboard.period.to')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            class="games-dashboard__filter"
          />
        </template>
        <AppSelect
          v-model="gameFilter"
          :items="gameItems"
          clearable
          density="compact"
          :label="t('alkokh.games.dashboard.gameFilter')"
          :placeholder="t('alkokh.games.plays.allGames')"
          class="games-dashboard__filter"
        />
        <VProgressCircular
          v-if="loading"
          indeterminate
          size="20"
          width="2"
          color="primary"
          class="ms-auto"
        />
      </VCardText>
    </VCard>

    <VAlert
      v-if="state === 'not_deployed'"
      type="info"
      variant="tonal"
      icon="tabler-info-circle"
      class="mb-4"
    >
      {{ t("alkokh.games.dashboard.states.notDeployed") }}
    </VAlert>

    <VAlert
      v-else-if="state === 'failed'"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ t("alkokh.games.dashboard.states.failed") }}
      <template #append>
        <VBtn
          size="small"
          variant="text"
          @click="load"
        >
          {{ t("alkokh.global.retry") }}
        </VBtn>
      </template>
    </VAlert>

    <template v-if="showData || cardLoading">
      <VRow class="mb-1">
        <VCol
          v-for="tile in tiles"
          :key="tile.key"
          cols="6"
          sm="4"
          lg="3"
        >
          <PetDashboardStatCard
            :title="t(`alkokh.games.dashboard.kpis.${tile.key}`)"
            :value="cardLoading ? '…' : tile.value"
            :icon="tile.icon"
            :color="tile.color"
          />
        </VCol>
      </VRow>

      <VRow>
        <VCol
          cols="12"
          lg="8"
        >
          <GamesTrendCard
            :data="data"
            :loading="cardLoading"
          />
        </VCol>
        <VCol
          cols="12"
          md="6"
          lg="4"
        >
          <GamesRedemptionCard
            :data="data"
            :loading="cardLoading"
          />
        </VCol>
        <VCol
          cols="12"
          md="6"
          lg="4"
        >
          <GamesAudienceCard
            :data="data"
            :loading="cardLoading"
          />
        </VCol>
        <VCol
          cols="12"
          lg="8"
        >
          <GamesHoursCard
            :data="data"
            :loading="cardLoading"
          />
        </VCol>
        <VCol
          cols="12"
          lg="7"
        >
          <GamesTopPrizesCard
            :data="data"
            :loading="cardLoading"
          />
        </VCol>
        <VCol
          cols="12"
          lg="5"
        >
          <GamesBranchesCard
            :data="data"
            :loading="cardLoading"
          />
        </VCol>
      </VRow>
    </template>
  </div>
</template>

<style scoped>
.games-dashboard__head {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  margin-block-end: 16px;
}

.games-dashboard__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.games-dashboard__filter {
  flex: 1 1 180px;
  max-inline-size: 260px;
}
</style>
